import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { IAssetTag, ITag } from "../../models/tag.interface";
import { environment } from "../../../../environments/environment";

export interface ICreateTagRequest {
    name: string;
    colour: string;
    is_quick_tag: boolean;
    description?: string;
}

export interface IUpdateTagItem {
    id: string;
    name?: string;
    colour?: string;
    is_quick_tag?: boolean;
    description?: string;
}

export interface IUpdateTagsRequest {
    tags: IUpdateTagItem[];
}

export interface IDeleteTagsRequest {
    tag_ids: string[];
}

export interface IAssignAssetTagsRequest {
    tag_ids: string[];
}

export interface IUnassignAssetTagsRequest {
    tag_ids: string[];
}

@Injectable({
    providedIn: "root"
})
export class TagService {
    readonly endpoints = {
        tags: `${environment.apiUrl}/tags`,
        assets: `${environment.apiUrl}/assets`,
    };

    private http: HttpClient = inject(HttpClient);

    createTag(body: ICreateTagRequest): Observable<ITag> {
        return this.http.post<ITag>(this.endpoints.tags, body);
    }

    getTags(quickOnly: boolean = false): Observable<ITag[]> {
        if (quickOnly) {
            return this.http.get<ITag[]>(this.endpoints.tags, { params: { quick_only: 'true' } });
        }
        return this.http.get<ITag[]>(this.endpoints.tags);
    }

    updateTags(body: IUpdateTagsRequest): Observable<ITag[]> {
        return this.http.put<ITag[]>(this.endpoints.tags, body);
    }

    deleteTags(body: IDeleteTagsRequest): Observable<void> {
        return this.http.request<void>('DELETE', this.endpoints.tags, { body });
    }

    getColours(): Observable<string[]> {
        return this.http.get<string[]>(`${this.endpoints.tags}/colours`);
    }

    getAssetTags(assetType: string, assetId: string): Observable<IAssetTag[]> {
        return this.http.get<IAssetTag[]>(`${this.endpoints.assets}/${encodeURIComponent(assetType)}/${encodeURIComponent(assetId)}/tags`);
    }

    assignAssetTags(assetType: string, assetId: string, body: IAssignAssetTagsRequest): Observable<IAssetTag[]> {
        return this.http.post<IAssetTag[]>(`${this.endpoints.assets}/${encodeURIComponent(assetType)}/${encodeURIComponent(assetId)}/tags`, body);
    }

    unassignAssetTags(assetType: string, assetId: string, body: IUnassignAssetTagsRequest): Observable<{ removed: number }> {
        return this.http.request<{ removed: number }>('DELETE', `${this.endpoints.assets}/${encodeURIComponent(assetType)}/${encodeURIComponent(assetId)}/tags`, { body });
    }
}
